/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import ModalAlert from '@/components/General/ModalAlert.jsx';
import Loader from '@/components/Loader.jsx';
import { api } from '@/utils/api.js';
import { apiNl } from '@/utils/apiNl.js';
import { EventType, InteractionType } from '@azure/msal-browser';
import {
	AuthenticatedTemplate,
	MsalProvider,
	UnauthenticatedTemplate,
	useMsal,
} from '@azure/msal-react';
import { ConfigProvider } from 'antd';

import { BrowserRouter as RouterPrincipal } from 'react-router-dom';
import AppRoutes from './routes/index.js';
import { PageLayout } from './ui.jsx';

import { connect, useDispatch, useSelector } from 'react-redux';
import './App.css';
import { getSecurityAction, acceptEulaAction } from './redux/slices/securitySlice';

import { hideModal } from '@/redux/slices/modalAlertSlice.js';

import { b2cPolicies } from './authConfig';
import ExpiredSessionModal from './components/Modal/ExpiredSessionModal.js';
import EulaModal from './components/Modal/EulaModal';
import { AppDispatch } from './redux/store.js';

const MainContent = () => {
	const { instance, accounts, inProgress } = useMsal();
	const dispatch: AppDispatch = useDispatch();
	const [showEulaModal, setShowEulaModal] = useState(false);
	const security = useSelector((state: any) => state.security);

	// Check if user is already authenticated on page load/refresh
	useEffect(() => {
		const userId = localStorage.getItem('userId');
		
		// If user is already authenticated but security data not loaded, fetch it
		if (userId && accounts.length > 0 && !security.array) {
			dispatch(getSecurityAction(userId));
		}
	}, [accounts, dispatch]);

	// Check if EULA modal should be shown after security data is loaded
	useEffect(() => {

		if (
			security.array &&
			security.array.requiresEulaAcceptance &&
			!security.array.hasAcceptedEula
		) {
			setShowEulaModal(true);
		}
	}, [security]);

	const handleEulaAccept = async () => {
		const userId = localStorage.getItem('userId');
		if (userId && security.array&& security.array.eulaVersion) {
			const success = await dispatch(
				acceptEulaAction(userId, security.array.eulaVersion)
			);
			if (success) {
				setShowEulaModal(false);
			}
		}
	};

	const handleEulaCancel = () => {
		setShowEulaModal(false);
		localStorage.clear();
		instance.logoutRedirect({
			postLogoutRedirectUri: '/',
		});
	};

	useEffect(() => {
		const callbackId = instance.addEventCallback((event: any) => {
			if (event.eventType === EventType.HANDLE_REDIRECT_START) {
				if (event.error && event.error.errorMessage.indexOf('AADB2C90118') > -1) {
					if (event.interactionType === InteractionType.Redirect) {
						instance.loginRedirect(b2cPolicies.authorities.forgotPassword);
					} else if (event.interactionType === InteractionType.Popup) {
						instance.loginPopup(b2cPolicies.authorities.forgotPassword).catch((e) => {
							return;
						});
					}
				}
			}

			if (event.eventType === EventType.LOGIN_SUCCESS) {
				if (event?.payload) {
					/**
					 * We need to reject id tokens that were not issued with the default sign-in policy.
					 * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
					 * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
					 */

					localStorage.setItem('userId', event?.payload.idTokenClaims.oid);

					api.defaults.headers.common = {
						Authorization: `Bearer ${event?.payload.accessToken}`,
					};
					apiNl.defaults.headers.common = {
						Authorization: `Bearer ${event?.payload.accessToken}`,
					};
					dispatch(getSecurityAction(event?.payload.idTokenClaims.oid));

					if (event.payload.idTokenClaims['acr'] === b2cPolicies.names.forgotPassword) {
						window.alert(
							'Password has been reset successfully. \nPlease sign-in with your new password',
						);
						return instance.logout();
					}
				}
			}
		});

		return () => {
			if (callbackId) {
				instance.removeEventCallback(callbackId);
			}
		};
	}, []);

	const securityData = localStorage.getItem('security');
	const organizationId = JSON.parse(securityData || '{}').organizationId;

	return (
		<div className="App">
			<EulaModal
				showModal={showEulaModal}
				onCancel={handleEulaCancel}
				onAccept={handleEulaAccept}
				eulaVersion={security.eulaVersion || 1}
			/>
			<AuthenticatedTemplate>
				<RouterPrincipal>
					<AppRoutes organizationId={organizationId} />
				</RouterPrincipal>
			</AuthenticatedTemplate>

			<UnauthenticatedTemplate>
				<div className="spinner">
					<span className="spinner-text"></span>
					<Loader show={true} />
				</div>
			</UnauthenticatedTemplate>
		</div>
	);
};

function App({ msalInstance, isLoading, modalAlertOptions, showExpiredSessionModal }) {
	const dispatch = useDispatch();
	return (
		<MsalProvider instance={msalInstance}>
				<Loader show={isLoading} />
				<ModalAlert
					{...modalAlertOptions}
					onClose={() => {
						dispatch(hideModal());
					}}
				/>
				<ExpiredSessionModal
					showModal={showExpiredSessionModal}
					closeModal={() => {
						localStorage.clear();
						msalInstance.logoutRedirect({
							postLogoutRedirectUri: '/',
						});
					}}
				/>
				<PageLayout>
					<ConfigProvider
						theme={{
							token: {
								colorPrimary: '#078CB3',
							},
							components: {
								Table: {
									rowSelectedBg: 'transparent',
								},
							},
						}}
					>
						<MainContent />
					</ConfigProvider>
				</PageLayout>
		</MsalProvider>
	);
}

function mapState(state) {
	return {
		isLoading: state.loader.currentStack > 0,
		modalAlertOptions: state.modalAlert.options,
		showExpiredSessionModal: state.modalAlert.showExpiredSessionModal,
	};
}

export default connect(mapState)(App);
